import { useState, useEffect, useContext, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { CartContext } from "../../contexts/CartContext";
import { AuthContext } from "../../contexts/AuthContext";
import { setAxiosInstance } from "../../axiosInstance";
import { formatVND } from "../../utils/format";
import axios from "axios";
import PageWrapper from "../../components/pageWrapper/PageWrapper";
import Modal from "../../components/Modal/Modal";

function OrderHistory() {
    const [searchQuery, setSearchQuery] = useState("");
    const [orders, setOrders] = useState([]);
    const [loading, setLoading] = useState(true);
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [updatingOrderId, setUpdatingOrderId] = useState(null);
    const navigate = useNavigate();
    const { addToCart } = useContext(CartContext);
    const { user } = useContext(AuthContext);
    const [totalItems, setTotalItems] = useState(0);
    const [status, setStatus] = useState("all");
    const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
    const [confirmAction, setConfirmAction] = useState(null); 
    const userId = user?.userId;

    useEffect(() => {
        if (!user || !userId) {
            navigate("/login");
            return;
        }
        if (user.token && user.role === "Customer") {
            setAxiosInstance(user.token);
        }
    }, [user, userId, navigate]);

    const fetchOrders = useCallback(
        async (query = "") => {
            try {
                setLoading(true);
                let url;
                if (query) {
                    url = `/api/Order/get-page-orders?emailOrPhone=${encodeURIComponent(query)}`;
                } else {
                    url = `/api/Order/get-page-orders?customerId=${userId}&page=${currentPage}&status=${status === "all" ? "" : status}`;
                }

                const response = await axios.get(url);
                setOrders(
                    Array.isArray(response.data.items) ? response.data.items : []
                );
                setTotalPages(response.data.totalPages || 1);
                setTotalItems(response.data.totalItems || 0);
            } catch (error) {
                console.error("Error fetching orders:", error);
                setOrders([]);
            } finally {
                setLoading(false);
            }
        },
        [userId, currentPage, status]
    );

    useEffect(() => {
        if (user && userId && user.role === "Customer") {
            fetchOrders();
        } else {
            setLoading(false);
        }
    }, [user, userId, fetchOrders]);

    const handleSearch = () => {
        if (user.role !== "Customer") return;
        setCurrentPage(1);
        fetchOrders(searchQuery);
    };

    const handleKeyPress = (e) => {
        if (e.key === "Enter") {
            handleSearch();
        }
    };

    const updateOrderStatus = async (orderId, newStatus) => {
        if (user.role !== "Customer") return;
        if (updatingOrderId) return;

        setUpdatingOrderId(orderId);
        try {
            if (!user?.token) {
                alert("Vui lòng đăng nhập lại.");
                navigate("/login");
                return;
            }

            const payload = { orderId, status: newStatus };
            await axios.put(`/api/Order/confirm-order-by-customer`, payload, {
                headers: {
                    "Content-Type": "application/json;odata.metadata=minimal;odata.streaming=true",
                    accept: "*/*",
                },
            });

            let attempts = 0;
            const maxAttempts = 15;
            const delay = 11000;
            let detailResponse;
            while (attempts < maxAttempts) {
                detailResponse = await axios.get(`/api/Order/get-order/${orderId}`);
                if (detailResponse.data.status === newStatus) {
                    break;
                }
                await new Promise((resolve) => setTimeout(resolve, delay));
                attempts++;
            }

            if (attempts === maxAttempts && detailResponse.data.status !== newStatus) {
                alert("Cập nhật trạng thái thất bại. Vui lòng thử lại sau.");
                return;
            }

            setOrders((prevOrders) =>
                prevOrders.map((order) =>
                    order.orderId === orderId ? detailResponse.data : order
                )
            );
        } catch (err) {
            alert("Không thể cập nhật trạng thái đơn hàng. Vui lòng thử lại.");
        } finally {
            setUpdatingOrderId(null);
        }
    };

    const handleConfirmAction = (orderId, newStatus, actionText) => {
        const validActionText = actionText || "thực hiện"; // Ensure actionText is always a string
        console.log("Opening modal with actionText:", validActionText); // Debug log
        setConfirmAction({ orderId, newStatus, actionText: validActionText });
        setIsConfirmModalOpen(true);
    };

    const handleConfirmModalClose = () => {
        console.log("Closing modal, confirmAction before reset:", confirmAction); // Debug log
        setIsConfirmModalOpen(false);
        setConfirmAction(null);
    };

    const handleConfirmModalSubmit = () => {
        if (confirmAction) {
            console.log("Submitting with actionText:", confirmAction.actionText); // Debug log
            updateOrderStatus(confirmAction.orderId, confirmAction.newStatus);
        }
        handleConfirmModalClose();
    };

    const formatCurrency = (amount) => {
        return amount !== undefined && amount !== null
            ? `${amount.toLocaleString("vi-VN")} đ`
            : "0 đ";
    };

    const formatDate = (dateString) => {
        const date = new Date(dateString);
        const time = date.toLocaleTimeString("vi-VN", { hour: "2-digit", minute: "2-digit" });
        const day = date.toLocaleDateString("vi-VN", { weekday: "long", day: "2-digit", month: "2-digit", year: "numeric" });
        return `${day} ${time}`;
    };

    const handleBuyAgain = (order) => {
        if (user.role !== "Customer") return;
        if (!order || !order.orderDetails || !Array.isArray(order.orderDetails)) {
            console.error("Invalid order data:", order);
            return;
        }

        order.orderDetails.forEach((item) => {
            if (!item.totalPrice || !item.quantity || !item.productName) {
                console.error("Invalid order detail:", item);
                return;
            }

            const selectedVariant = {
                variantId: item.variantId || null,
                size: item.size || null,
                color: item.color || null,
                price: item.totalPrice / item.quantity,
                discount: item.productDiscount || 0,
                quantity: item.quantity + 10,
            };

            const product = {
                id: item.productId || item.orderDetailId || Date.now(),
                name: item.productName,
                image: item.image || null,
                quantity: item.quantity,
                price: item.totalPrice / item.quantity,
                finalPrice: item.totalPrice / item.quantity,
                originalPrice: item.totalPrice / item.quantity,
                discount: item.productDiscount || 0,
                selectedVariant:
                    selectedVariant.size || selectedVariant.color
                        ? selectedVariant
                        : null,
                quantityInStock: item.quantity + 10,
            };

            addToCart(product);
        });

        navigate("/cart");
    };

    const handlePageChange = (newPage) => {
        if (user.role !== "Customer") return;
        if (newPage > 0 && newPage <= totalPages) {
            setCurrentPage(newPage);
        }
    };

    return (
        <PageWrapper title="Lịch sử đơn hàng">
            <div className="container mx-auto px-4 sm:px-6 lg:px-12 py-8 max-w-[1900px] bg-white rounded-lg shadow-sm">
                {loading && <div className="text-center text-gray-600">Đang tải...</div>}
                {!loading && (
                    <>
                        <h2 className="text-2xl sm:text-3xl font-semibold text-gray-800 mb-6">Lịch sử đơn hàng</h2>
                        {user.role !== "Customer" ? (
                            <div className="text-center text-gray-600 py-8">Không có thông tin dành cho quản trị viên.</div>
                        ) : (
                            <>
                                <div className="flex flex-col sm:flex-row gap-4 mb-6">
                                    <select
                                        value={status}
                                        onChange={(e) => setStatus(e.target.value)}
                                        className="px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 w-full sm:w-auto"
                                    >
                                        <option value="all">Tất cả</option>
                                        <option value="Pending">Đang xử lý</option>
                                        <option value="Completed">Hoàn thành</option>
                                        <option value="Cancelled">Đã hủy</option>
                                    </select>
                                    <input
                                        type="text"
                                        placeholder="Nhập email hoặc số điện thoại để tìm kiếm..."
                                        className="flex-1 px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                                        value={searchQuery}
                                        onChange={(e) => setSearchQuery(e.target.value)}
                                        onKeyPress={handleKeyPress}
                                    />
                                    <button
                                        className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                                        onClick={handleSearch}
                                    >
                                        <svg
                                            xmlns="http://www.w3.org/2000/svg"
                                            width="16"
                                            height="16"
                                            viewBox="0 0 24 24"
                                            fill="none"
                                            stroke="currentColor"
                                            strokeWidth="2"
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                        >
                                            <circle cx="11" cy="11" r="8"></circle>
                                            <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
                                        </svg>
                                        Tìm kiếm
                                    </button>
                                </div>

                                <div className="border rounded-lg overflow-hidden">
                                    <div className="grid grid-cols-3 sm:grid-cols-5 lg:grid-cols-7 gap-4 p-4 bg-gray-50 font-semibold text-gray-700 text-sm">
                                        <div>Mã đơn hàng</div>
                                        <div className="hidden sm:block">Tên người đặt</div>
                                        <div>Sản phẩm</div>
                                        <div className="hidden lg:block">Tổng tiền</div>
                                        <div className="hidden sm:block">Ngày đặt hàng</div>
                                        <div>Trạng thái</div>
                                        <div>Hành động</div>
                                    </div>

                                    {orders.length === 0 ? (
                                        <div className="text-center py-8 text-gray-600">Không có đơn hàng nào.</div>
                                    ) : (
                                        orders.map((order) => (
                                            <div key={order.orderId} className="grid grid-cols-3 sm:grid-cols-5 lg:grid-cols-7 gap-4 p-4 hover:bg-gray-50 items-center text-sm border-b">
                                                <div className="text-blue-600 font-medium truncate">{order.orderId}</div>
                                                <div className="hidden sm:block truncate">{order.fullName || "N/A"}</div>
                                                <div className="truncate">
                                                    {order.orderDetails?.map((item) => (
                                                        <div key={item.orderDetailId} className="truncate" title={item.productName}>
                                                            - {item.productName} x {item.quantity}
                                                        </div>
                                                    ))}
                                                </div>
                                                <div className="hidden lg:block font-medium">{formatVND(order.totalAmount)}</div>
                                                <div className="hidden sm:block truncate">{formatDate(order.orderDate)}</div>
                                                <div>
                                                    <span
                                                        className={`inline-block px-2 py-1 rounded-full text-xs font-medium truncate ${
                                                            order.status?.toLowerCase() === "completed"
                                                                ? "bg-green-100 text-green-800"
                                                                : order.status?.toLowerCase() === "pending"
                                                                    ? "bg-orange-100 text-orange-800"
                                                                    : order.status?.toLowerCase() === "cancelled"
                                                                        ? "bg-red-100 text-red-800"
                                                                        : order.status?.toLowerCase() === "received"
                                                                            ? "bg-blue-100 text-blue-800"
                                                                            : "bg-gray-100 text-gray-800"
                                                        }`}
                                                    >
                                                        {order.status?.toLowerCase() === "completed"
                                                            ? "Đã hoàn thành"
                                                            : order.status?.toLowerCase() === "pending"
                                                                ? "Chờ xác nhận"
                                                                : order.status?.toLowerCase() === "cancelled"
                                                                    ? "Đã bị huỷ"
                                                                    : order.status?.toLowerCase() === "received"
                                                                        ? "Đã nhận hàng"
                                                                        : "Không xác định"}
                                                    </span>
                                                </div>
                                                <div className="flex gap-2 flex-wrap">
                                                    {order.status?.toLowerCase() === "completed" && (
                                                        <>
                                                            <button
                                                                className="flex items-center gap-1 px-2 py-1 bg-blue-600 text-white rounded hover:bg-blue-700 text-xs transition-colors"
                                                                onClick={() => handleBuyAgain(order)}
                                                            >
                                                                <svg
                                                                    xmlns="http://www.w3.org/2000/svg"
                                                                    width="12"
                                                                    height="12"
                                                                    viewBox="0 0 24 24"
                                                                    fill="none"
                                                                    stroke="currentColor"
                                                                    strokeWidth="2"
                                                                    strokeLinecap="round"
                                                                    strokeLinejoin="round"
                                                                >
                                                                    <circle cx="9" cy="21" r="1"></circle>
                                                                    <circle cx="20" cy="21" r="1"></circle>
                                                                    <path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6"></path>
                                                                </svg>
                                                                Mua lại
                                                            </button>
                                                            <button
                                                                className="flex items-center gap-1 px-2 py-1 bg-green-600 text-white rounded hover:bg-green-700 text-xs transition-colors"
                                                                onClick={() => handleConfirmAction(order.orderId, "Received", "xác nhận đã nhận")}
                                                                disabled={updatingOrderId === order.orderId}
                                                            >
                                                                <svg
                                                                    xmlns="http://www.w3.org/2000/svg"
                                                                    width="12"
                                                                    height="12"
                                                                    viewBox="0 0 24 24"
                                                                    fill="none"
                                                                    stroke="currentColor"
                                                                    strokeWidth="2"
                                                                    strokeLinecap="round"
                                                                    strokeLinejoin="round"
                                                                >
                                                                    <polyline points="20 6 9 17 4 12"></polyline>
                                                                </svg>
                                                                Đã nhận
                                                            </button>
                                                        </>
                                                    )}
                                                    {order.status?.toLowerCase() === "received" && (
                                                        <button
                                                            className="flex items-center gap-1 px-2 py-1 bg-blue-600 text-white rounded hover:bg-blue-700 text-xs transition-colors"
                                                            onClick={() => handleBuyAgain(order)}
                                                        >
                                                            <svg
                                                                xmlns="http://www.w3.org/2000/svg"
                                                                width="12"
                                                                height="12"
                                                                viewBox="0 0 24 24"
                                                                fill="none"
                                                                stroke="currentColor"
                                                                strokeWidth="2"
                                                                strokeLinecap="round"
                                                                strokeLinejoin="round"
                                                            >
                                                                <circle cx="9" cy="21" r="1"></circle>
                                                                <circle cx="20" cy="21" r="1"></circle>
                                                                <path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6"></path>
                                                            </svg>
                                                            Mua lại
                                                        </button>
                                                    )}
                                                    {order.status?.toLowerCase() === "pending" && (
                                                        <button
                                                            className="flex items-center gap-1 px-2 py-1 bg-red-600 text-white rounded hover:bg-red-700 text-xs transition-colors"
                                                            onClick={() => handleConfirmAction(order.orderId, "Cancelled", "hủy")}
                                                            disabled={updatingOrderId === order.orderId}
                                                        >
                                                            <svg
                                                                xmlns="http://www.w3.org/2000/svg"
                                                                width="12"
                                                                height="12"
                                                                viewBox="0 0 24 24"
                                                                fill="none"
                                                                stroke="currentColor"
                                                                strokeWidth="2"
                                                                strokeLinecap="round"
                                                                strokeLinejoin="round"
                                                            >
                                                                <line x1="18" y1="6" x2="6" y2="18"></line>
                                                                <line x1="6" y1="6" x2="18" y2="18"></line>
                                                            </svg>
                                                            Hủy
                                                        </button>
                                                    )}
                                                </div>
                                            </div>
                                        ))
                                    )}
                                </div>

                                <div className="flex flex-col sm:flex-row justify-between items-center mt-6 gap-4">
                                    <div className="text-gray-600 text-sm">
                                        Hiển thị {orders.length} đơn hàng (Tổng: {totalItems})
                                    </div>
                                    {totalPages > 1 && (
                                        <div className="flex items-center gap-4">
                                            <button
                                                className="px-3 py-1 border rounded-lg text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100 transition-colors"
                                                disabled={currentPage === 1}
                                                onClick={() => handlePageChange(currentPage - 1)}
                                            >
                                                Trước
                                            </button>
                                            <span className="text-gray-600 text-sm">
                                                Trang {currentPage} / {totalPages}
                                            </span>
                                            <button
                                                className="px-3 py-1 border rounded-lg text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100 transition-colors"
                                                disabled={currentPage === totalPages}
                                                onClick={() => handlePageChange(currentPage + 1)}
                                            >
                                                Sau
                                            </button>
                                        </div>
                                    )}
                                </div>
                                <Modal
                                    isOpen={isConfirmModalOpen}
                                    onClose={handleConfirmModalClose}
                                    title={`Đồng ý ${confirmAction?.actionText || "thực hiện"} đơn hàng`}
                                >
                                    <div className="p-6">
                                        <p className="text-gray-700 mb-6">
                                            Bạn có chắc chắn muốn {confirmAction?.actionText || "thực hiện hành động với"} đơn hàng này không?
                                        </p>
                                        <div className="flex justify-end space-x-4">
                                            <button
                                                onClick={handleConfirmModalClose}
                                                className="px-4 py-2 bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300"
                                                aria-label="Hủy hành động"
                                            >
                                                Hủy
                                            </button>
                                            <button
                                                onClick={handleConfirmModalSubmit}
                                                className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600"
                                                aria-label="Xác nhận hành động"
                                            >
                                                Xác nhận
                                            </button>
                                        </div>
                                    </div>
                                </Modal>
                            </>
                        )}
                    </>
                )}
            </div>
        </PageWrapper>
    );
}

export default OrderHistory;